// Patient Service
// Complete CRUD operations for patient management

import { 
  collection, 
  doc, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  getDoc, 
  getDocs, 
  query, 
  where, 
  orderBy,
  Timestamp 
} from 'firebase/firestore';
import { db } from '../firebase';

export class PatientService {
  constructor() {
    this.patientsCollection = collection(db, 'patients');
  }
  
  /**
   * Create a new patient record
   */
  async createPatient(patientData, userId) {
    const now = Timestamp.now();
    
    const patient = {
      ...patientData,
      status: patientData.status || 'admitted',
      riskFactorIds: patientData.riskFactorIds || [],
      conditions: patientData.conditions || [],
      createdAt: now,
      createdBy: userId,
      updatedAt: now,
      updatedBy: userId
    };
    
    // Remove id field if it exists (will be auto-generated by Firestore)
    delete patient.id;
    
    const docRef = await addDoc(this.patientsCollection, patient);
    
    console.log(`✅ Created patient: ${docRef.id}`);
    return docRef.id;
  }
  
  /**
   * Get patient by ID
   */
  async getPatient(patientId) {
    const docRef = doc(this.patientsCollection, patientId);
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      return { id: docSnap.id, ...docSnap.data() };
    }
    return null;
  }
  
  /**
   * Update patient
   */
  async updatePatient(patientId, updates, userId) {
    const docRef = doc(this.patientsCollection, patientId);
    
    await updateDoc(docRef, {
      ...updates,
      updatedAt: Timestamp.now(),
      updatedBy: userId
    });
    
    console.log(`✅ Updated patient: ${patientId}`);
  }
  
  /**
   * Delete patient
   */
  async deletePatient(patientId) {
    const docRef = doc(this.patientsCollection, patientId);
    await deleteDoc(docRef);
    
    console.log(`✅ Deleted patient: ${patientId}`);
  }
  
  /**
   * Get all patients for an organization.
   * @param {string} organizationId — required; throws if missing
   */
  async getAllPatients(organizationId) {
    if (!organizationId) {
      throw new Error('[PatientService] organizationId is required for getAllPatients');
    }
    const q = query(
      this.patientsCollection,
      where('organizationId', '==', organizationId),
      orderBy('admissionDate', 'desc')
    );

    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  }
  
  /**
   * Get patients by department
   */
  async getPatientsByDepartment(departmentId) {
    const q = query(
      this.patientsCollection,
      where('departmentId', '==', departmentId),
      where('status', '==', 'admitted'),
      orderBy('admissionDate', 'desc')
    );
    
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  }
  
  /**
   * Get patients by room
   */
  async getPatientsByRoom(roomId) {
    const q = query(
      this.patientsCollection,
      where('roomId', '==', roomId),
      where('status', '==', 'admitted'),
      orderBy('admissionDate', 'desc')
    );
    
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  }
  
  /**
   * Get patient by bed
   */
  async getPatientByBed(bedId) {
    const q = query(
      this.patientsCollection,
      where('bedId', '==', bedId),
      where('status', '==', 'admitted')
    );
    
    const snapshot = await getDocs(q);
    
    if (snapshot.empty) {
      return null;
    }
    
    // Should only be one patient per bed
    const doc = snapshot.docs[0];
    return { id: doc.id, ...doc.data() };
  }
  
  /**
   * Get patients by status for an organization.
   * @param {string} status
   * @param {string} organizationId — required; throws if missing
   */
  async getPatientsByStatus(status, organizationId) {
    if (!organizationId) {
      throw new Error('[PatientService] organizationId is required for getPatientsByStatus');
    }
    const q = query(
      this.patientsCollection,
      where('organizationId', '==', organizationId),
      where('status', '==', status),
      orderBy('admissionDate', 'desc')
    );

    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  }
  
  /**
   * Get patients with a specific risk factor, scoped to organization.
   * @param {string} riskFactorId
   * @param {string} organizationId — required
   */
  async getPatientsWithRiskFactor(riskFactorId, organizationId) {
    const allPatients = await this.getAllPatients(organizationId);

    return allPatients.filter(patient =>
      patient.riskFactorIds && patient.riskFactorIds.includes(riskFactorId)
    );
  }
  
  /**
   * Get high-risk patients (those with multiple risk factors), scoped to organization.
   * @param {number} minimumRiskFactors
   * @param {string} organizationId — required
   */
  async getHighRiskPatients(minimumRiskFactors = 2, organizationId) {
    const allPatients = await this.getAllPatients(organizationId);

    return allPatients.filter(patient =>
      patient.status === 'admitted' &&
      patient.riskFactorIds &&
      patient.riskFactorIds.length >= minimumRiskFactors
    );
  }
  
  /**
   * Add risk factor to patient
   */
  async addRiskFactor(patientId, riskFactorId, userId) {
    const patient = await this.getPatient(patientId);
    if (!patient) throw new Error('Patient not found');
    
    const updatedRiskFactors = [...(patient.riskFactorIds || [])];
    if (!updatedRiskFactors.includes(riskFactorId)) {
      updatedRiskFactors.push(riskFactorId);
    }
    
    await this.updatePatient(patientId, {
      riskFactorIds: updatedRiskFactors
    }, userId);
    
    console.log(`✅ Added risk factor ${riskFactorId} to patient ${patientId}`);
  }
  
  /**
   * Remove risk factor from patient
   */
  async removeRiskFactor(patientId, riskFactorId, userId) {
    const patient = await this.getPatient(patientId);
    if (!patient) throw new Error('Patient not found');
    
    const updatedRiskFactors = (patient.riskFactorIds || []).filter(
      id => id !== riskFactorId
    );
    
    await this.updatePatient(patientId, {
      riskFactorIds: updatedRiskFactors
    }, userId);
    
    console.log(`✅ Removed risk factor ${riskFactorId} from patient ${patientId}`);
  }
  
  /**
   * Update patient risk factors (replace all)
   */
  async updateRiskFactors(patientId, riskFactorIds, userId) {
    await this.updatePatient(patientId, {
      riskFactorIds: riskFactorIds || []
    }, userId);
    
    console.log(`✅ Updated risk factors for patient ${patientId}`);
  }
  
  /**
   * Discharge patient
   */
  async dischargePatient(patientId, userId, dischargeNotes = '') {
    await this.updatePatient(patientId, {
      status: 'discharged',
      dischargeDate: Timestamp.now(),
      dischargeNotes
    }, userId);
    
    console.log(`✅ Discharged patient: ${patientId}`);
  }
  
  /**
   * Transfer patient to different location
   */
  async transferPatient(patientId, newBedId, newRoomId, newDepartmentId, userId, transferReason = '') {
    const patient = await this.getPatient(patientId);
    if (!patient) throw new Error('Patient not found');
    
    const transferHistory = patient.transferHistory || [];
    transferHistory.push({
      fromBedId: patient.bedId,
      fromRoomId: patient.roomId,
      fromDepartmentId: patient.departmentId,
      toBedId: newBedId,
      toRoomId: newRoomId,
      toDepartmentId: newDepartmentId,
      transferredAt: Timestamp.now(),
      transferredBy: userId,
      reason: transferReason
    });
    
    await this.updatePatient(patientId, {
      bedId: newBedId,
      roomId: newRoomId,
      departmentId: newDepartmentId,
      transferHistory
    }, userId);
    
    console.log(`✅ Transferred patient ${patientId} to bed ${newBedId}`);
  }
  
  /**
   * Get patient statistics for an organization, optionally filtered by department.
   * @param {string|null} departmentId
   * @param {string} organizationId — required when departmentId is null
   */
  async getPatientStatistics(departmentId = null, organizationId = null) {
    const patients = departmentId
      ? await this.getPatientsByDepartment(departmentId)
      : await this.getAllPatients(organizationId);
    
    const admitted = patients.filter(p => p.status === 'admitted');
    const highRisk = admitted.filter(p => 
      p.riskFactorIds && p.riskFactorIds.length >= 2
    );
    
    return {
      total: patients.length,
      admitted: admitted.length,
      discharged: patients.filter(p => p.status === 'discharged').length,
      transferred: patients.filter(p => p.status === 'transferred').length,
      highRisk: highRisk.length,
      withIsolation: admitted.filter(p => 
        p.isolationLevel && p.isolationLevel !== 'None'
      ).length,
      avgRiskFactors: admitted.length > 0
        ? admitted.reduce((sum, p) => sum + (p.riskFactorIds?.length || 0), 0) / admitted.length
        : 0
    };
  }
}

export const patientService = new PatientService();


